import { resolvePetId } from '../../data/pets';
/**
 * Which pets and cosmetics have a 3D model on the site, from the manifest the
 * iOS app's sync-3d-models.py writes, plus a snapshot renderer for grids.
 *
 * A grid of live WebGL previews would open a context per card, and browsers
 * stop at about sixteen. So grids show snapshots: one shared renderer draws
 * each model once, three-quarters on, into a picture that is cached for the
 * visit. Detail pages still get the live, turning model.
 */
let manifestPromise = null;

const json = (url, empty) => fetch(url).then((r) => (r.ok ? r.json() : empty)).catch(() => empty);

/** The pets with a model (the app's manifest) and the cosmetics with one
 * (every model on the site, listed in /cosmetics/models/index.json). */
export function loadModelManifest() {
  if (!manifestPromise) {
    manifestPromise = Promise.all([
      json('/pets/models/manifest.json', { pets: [] }),
      json('/cosmetics/models/index.json', { cosmetics: [] }),
    ]).then(([pets, cosmetics]) => ({
      pets: new Set((pets.pets || []).map((p) => (typeof p === 'string' ? p : p.id))),
      cosmetics: new Set(cosmetics.cosmetics || []),
    }));
  }
  return manifestPromise;
}

export function petModelUrls(petId) {
  const id = resolvePetId(petId);
  return { id, geoUrl: `/pets/models/${id}.geo.json`, textureUrl: `/pets/textures/${id}.png` };
}

export function cosmeticModelUrls(cosmeticId) {
  const id = String(cosmeticId).replace(/^unlock_/, '');
  return { id, geoUrl: `/cosmetics/models/${id}.geo.json`, textureUrl: `/cosmetics/textures/${id}.png` };
}

let shared = null; // { THREE, renderer, scene, camera, lights }
const snapshots = new Map(); // key -> Promise<string|null>
let queue = Promise.resolve();

async function sharedRenderer(size) {
  if (!shared) {
    const THREE = await import('three');
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(1);
    renderer.setClearColor(0x000000, 0);
    const scene = new THREE.Scene();
    scene.add(new THREE.AmbientLight(0xffffff, 0.7));
    const key = new THREE.DirectionalLight(0xffffff, 0.85);
    key.position.set(8, 12, 6);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0xe040fb, 0.35);
    rim.position.set(-6, 4, -8);
    scene.add(rim);
    const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 1000);
    shared = { THREE, renderer, scene, camera };
  }
  shared.renderer.setSize(size, size, false);
  return shared;
}

/**
 * Centres a model on its own bounds, turned three-quarters, and puts the
 * camera where it fills the frame: the loader leaves the origin at the
 * model's feet, so aiming at the origin left every model small and low.
 * Returns the group to add to the scene.
 */
export function frame(THREE, object, camera, turn = 0) {
  const pivot = new THREE.Group();
  const box = new THREE.Box3().setFromObject(object);
  const centre = box.getCenter(new THREE.Vector3());
  object.position.sub(centre);
  pivot.add(object);
  pivot.rotation.y = turn;
  const radius = box.getBoundingSphere(new THREE.Sphere()).radius || 8;
  // A sphere is roomier than the model inside it, so a little closer.
  const distance = radius / Math.sin((camera.fov * Math.PI) / 180 / 2) * 0.8;
  // From the front: the models face -Z, as in the app and the mod, so the
  // camera sits on that side (from +Z it saw every pet from behind).
  const dir = new THREE.Vector3(0.55, 0.38, -0.74).normalize();
  camera.position.copy(dir.multiplyScalar(distance));
  camera.near = distance / 100;
  camera.far = distance * 10;
  camera.updateProjectionMatrix();
  camera.lookAt(0, 0, 0);
  return pivot;
}

/** A still of a model, as a data URL, or null if it will not load. */
export function modelSnapshot(geoUrl, textureUrl, size = 256) {
  const key = `${geoUrl}|${size}`;
  if (!snapshots.has(key)) {
    // One at a time through the shared renderer.
    const job = queue.then(async () => {
      try {
        const { loadBedrockGeometry } = await import('../release/bedrockGeoLoader');
        const [{ renderer, scene, camera }, result] = await Promise.all([
          sharedRenderer(size),
          loadBedrockGeometry(geoUrl, textureUrl),
        ]);
        const root = frame(shared.THREE, result.object, camera);
        scene.add(root);
        renderer.render(scene, camera);
        const url = renderer.domElement.toDataURL('image/png');
        scene.remove(root);
        root.traverse((obj) => {
          if (obj.geometry) obj.geometry.dispose();
          if (obj.material) {
            if (obj.material.map) obj.material.map.dispose();
            obj.material.dispose();
          }
        });
        return url;
      } catch (_) {
        return null;
      }
    });
    queue = job.catch(() => null);
    snapshots.set(key, job);
  }
  return snapshots.get(key);
}
